import React, { useRef, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { marketPricePageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { useLayout } from '../../../hooks/screen/useLayout';
import { MainHead } from '../../../components/common/head/MainHead';
import { RecommendedSearchKeyword } from './components/RecommendedSearchKeyword';
import { isIOS } from 'react-device-detect';
import styled from 'styled-components';
import { marketPriceFilterShortcutInfos } from '../../../constants/filter/shortCut/filterShortcut.constant';
import { Button } from '../../../components/common/buttons/Button';
import { ampli } from '../../../ampli';
import { useDispatch } from 'react-redux';
import { filterSlice } from '../../../store/filterSlice';
import { useGetDownloadAppModalHeight } from '../../../hooks/modal/useGetDownloadAppModalHeight';

const filterSuggestions = [
  { filterKey: 'medianPrice', title: '가격대로 보기' },
  { filterKey: 'engineOutputInPs', title: '마력대로 보기' },
  { filterKey: 'filter_brand', title: '브랜드별로 보기' },
] as const;

export const MarketPriceSearchPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [searchParams] = useSearchParams();
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const downloadAppModalHeight = useGetDownloadAppModalHeight();

  useLayout({
    fixedHeader: (
      <MainHead
        backButton={{ isShow: true }}
        searchInput={{
          searchKeyword,
          setSearchKeyword,
          inputRef: searchInputRef,
          searchInputType: 'marketPrice',
        }}
      />
    ),
    fixedHeaderHeight: 48,
    fixedFooter: <></>,
    fixedFooterHeight: 0,
  });

  useEffect(() => {
    const keyword = searchParams.get('keyword');
    setSearchKeyword(keyword ?? '');
  }, [searchParams]);

  useEffect(() => {
    const inputAutoFocusTimer = setTimeout(() => {
      if (!isIOS) {
        searchInputRef.current?.blur();
        searchInputRef.current?.focus();
      }
    }, 100);

    return () => clearTimeout(inputAutoFocusTimer);
  }, []);

  const handleFilterSuggestionClick = (filterKey: string, title: string, position: string) => {
    dispatch(filterSlice.actions.setSelectedFilterKey({ filterType: 'marketPriceFilter', value: filterKey }));
    navigate(marketPricePageInfo.listPage.path, { replace: true });
    ampli.pvmClickMarketPriceFilterSuggestion({ title, position });
  };

  const handleContainerTouchStart = (e: TouchEvent) => {
    if (searchInputRef.current && !searchInputRef.current.contains(e.target as Node)) {
      searchInputRef.current.blur();
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) container.addEventListener('touchstart', handleContainerTouchStart);

    return () => {
      if (container) container.removeEventListener('touchstart', handleContainerTouchStart);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>{marketPricePageInfo.searchPage.title}</title>
      </Helmet>

      <Container ref={containerRef} downloadAppModalHeight={downloadAppModalHeight}>
        {!searchKeyword && (
          <ContentsContainer>
            <QuickRecommendationSection>
              <QuickTitle>{'간편 추천'}</QuickTitle>

              <QuickItemContainer>
                {marketPriceFilterShortcutInfos.shortcutButtonInfos.map((shortcutInfo, index) => {
                  return (
                    <QuickItem
                      key={shortcutInfo.title}
                      onClick={() => {
                        navigate(shortcutInfo.navigateTo, { replace: true });
                        ampli.pvmClickMarketPriceQuickRecommendation({
                          title: shortcutInfo.title,
                          position: String(index),
                        });
                      }}
                    >
                      <QuickItemText>{shortcutInfo.title}</QuickItemText>
                    </QuickItem>
                  );
                })}
              </QuickItemContainer>
            </QuickRecommendationSection>

            <FilterSuggestionSection>
              <FilterSuggestionTitle>{'이렇게 찾아보는 건 어때요?'}</FilterSuggestionTitle>

              <FilterSuggestionItemContainer>
                {filterSuggestions.map(({ filterKey, title }, index) => (
                  <Button
                    key={filterKey}
                    borderType={'round'}
                    color={'outlinedGreyLight'}
                    size={'md'}
                    onClick={() => handleFilterSuggestionClick(filterKey, title, String(index))}
                    text={{
                      content: title,
                      size: 'md',
                    }}
                    icon={{
                      position: 'right',
                      fileName: 'AngleRight',
                      size: 'Medium',
                      fill: '#505363',
                    }}
                  />
                ))}
              </FilterSuggestionItemContainer>
            </FilterSuggestionSection>
          </ContentsContainer>
        )}

        {searchKeyword && <RecommendedSearchKeyword searchKeyword={searchKeyword} />}
      </Container>
    </>
  );
};

const Container = styled.div<{ downloadAppModalHeight: number }>`
  width: 100%;
  min-height: ${({ downloadAppModalHeight }) => `calc(100vh - 48px - ${downloadAppModalHeight}px)`};
`;

const ContentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
  align-self: stretch;
`;

const QuickRecommendationSection = styled.div`
  display: flex;
  padding: 24px 16px 32px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const QuickTitle = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 23.4px */
`;

const QuickItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-content: flex-start;
  gap: 8px;
  align-self: stretch;
  flex-wrap: wrap;
`;

const QuickItem = styled.div`
  display: flex;
  height: 40px;
  max-height: 40px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 4px;

  border-radius: var(--radius-round, 80px);
  background: var(--Light-Scheme-Monochrome-grey20, #ececf3);

  cursor: pointer;
`;

const QuickItemText = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* System/Large/l-medium */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 13.6px */
`;

const FilterSuggestionSection = styled.div`
  display: flex;
  padding: 0px 16px 32px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const FilterSuggestionTitle = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 23.4px */
`;

const FilterSuggestionItemContainer = styled.div`
  display: flex;
  align-items: flex-start;
  align-content: flex-start;
  gap: 8px;
  align-self: stretch;
  flex-wrap: wrap;
`;
